import { Tree } from 'antd';
import type { DirectoryTreeProps } from 'antd/es/tree';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import FilePreviewPanel from './FilePreviewPanel';
import {
  IFlatNode,
  IFolderNode,
  convertFlatToTree,
  convertToTreeData,
} from './utils';

const { DirectoryTree } = Tree;

interface IFolderPreviewProps {
  data?: IFolderNode;
  flatData?: IFlatNode[];
  height?: number | string;
  emptyStateText?: string;
  modelInstanceId?: string;
  downloadButtonText?: string;
  previewButtonNotAvailableText?: string;
  style?: React.CSSProperties;
}

const FolderPreview: React.FC<IFolderPreviewProps> = ({
  style,
  data,
  flatData,
  height = 800,
  modelInstanceId,
  emptyStateText = 'Select a file to preview',
  downloadButtonText = 'Download',
  previewButtonNotAvailableText = 'Preview not available for this file type',
}) => {
  const [selectedFile, setSelectedFile] = useState<IFolderNode | null>(null);

  const { treeData, nodeMap } = useMemo(() => {
    const resolved =
      data ??
      (flatData && Array.isArray(flatData)
        ? convertFlatToTree(flatData)
        : null);
    if (!resolved)
      return { treeData: [], nodeMap: new Map<string, IFolderNode>() };

    const map = new Map<string, IFolderNode>();
    const tree = convertToTreeData(resolved, map);

    return { treeData: [tree], nodeMap: map };
  }, [data, flatData]);

  const onSelect: DirectoryTreeProps['onSelect'] = (_keys, info) => {
    const key = String(info.node.key);
    const node = nodeMap.get(key);
    if (node) setSelectedFile(node);
  };

  return (
    <Container $height={height} style={style}>
      <TreePanel>
        <DirectoryTree treeData={treeData} onSelect={onSelect} />
      </TreePanel>
      <PreviewPanel>
        {selectedFile ? (
          <FilePreviewPanel
            file={selectedFile}
            modelInstanceId={modelInstanceId}
            downloadButtonText={downloadButtonText}
            previewButtonNotAvailableText={previewButtonNotAvailableText}
          />
        ) : (
          <EmptyState>{emptyStateText}</EmptyState>
        )}
      </PreviewPanel>
    </Container>
  );
};

export default FolderPreview;

const Container = styled.div<{ $height: number | string }>`
  display: flex;
  height: ${({ $height }) =>
    typeof $height === 'number' ? `${$height}px` : $height};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface};
`;

const TreePanel = styled.div`
  width: 320px;
  flex-shrink: 0;
  overflow-y: auto;
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${({ theme }) => theme.spacing.sm};
`;

const PreviewPanel = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;
